import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/contexts/app-context";
import { useChapters } from "@/hooks/useChapters";
import { CardType, Chapter } from "@/pages/timeline/types";
import { timelineService } from "@/services/supabase/timeline";
import { TimelineToolOutput } from "@/types/ai-schemas";
import { ToolCallMessagePartComponent } from "@assistant-ui/react";
import {
    Check,
    ChevronDown,
    ChevronUp,
    GitBranch,
    Loader2,
    Play,
    RefreshCw,
    Target
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const CreateTimelineTool: ToolCallMessagePartComponent = ({
    result,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const { storyId: routeStoryId, activeStoryId } = useApp();
    const storyId = routeStoryId || activeStoryId;

    // Use cached chapters query instead of direct service call
    const { data: chapters = [] } = useChapters(storyId ? String(storyId) : undefined);

    // State to hold the editable values
    const [editableTimeline, setEditableTimeline] = useState<TimelineToolOutput | null>(null);
    // Track if user has manually edited to prevent overwrites from streaming updates
    const [hasUserEdited, setHasUserEdited] = useState(false);

    const isLoading = result === undefined;

    // Parse result and unwrap tool result wrapper: {success, message, data: {...}} → data
    const rawData = typeof result === 'string' ? tryParseJSON(result) : result;
    const unwrappedData = (rawData && typeof rawData === 'object' && 'data' in rawData && rawData.data && typeof rawData.data === 'object')
        ? rawData.data
        : rawData;

    // Normalize and sync state
    useEffect(() => {
        if (hasUserEdited) return; // Don't overwrite user edits

        if (unwrappedData) {
            setEditableTimeline(unwrappedData);
        }
    }, [unwrappedData, hasUserEdited]);


    const handleAction = async () => {
        if (!storyId) {
            toast.error("Story context not found.");
            return;
        }

        if (!editableTimeline) return;

        setIsProcessing(true);
        try {
            const isUpdate = editableTimeline._action === 'update';
            const beats = editableTimeline.beats || [];

            if (isUpdate) {
                if (!editableTimeline.id) throw new Error("Missing Timeline ID for update");
                const tid = String(editableTimeline.id);

                // Update Timeline metadata
                await timelineService.updatePlotLine(tid, {
                    name: editableTimeline.name,
                    description: editableTimeline.description
                });

                // Update/Create Beats
                const beatPromises = beats.map(async (beat) => {
                    const realChapterId = resolveChapterId(beat.chapterId, chapters);

                    if (beat.id) {
                        // Update existing beat
                        return timelineService.updatePlotCard(beat.id, {
                            title: beat.name || beat.beatType || beat.title, // Schema says beatType, assuming title maps to one of them or name
                            content: beat.description,
                            chapter_id: realChapterId ? Number(realChapterId) : undefined,
                            type: (beat.type as CardType) || 'plot' // Use provided type or default
                        });
                    } else {
                        // Create new beat
                        return timelineService.createPlotCard(
                            tid, // timelineId
                            tid, // plotLineId (assuming same for now as per service logic)
                            beat.title || beat.beatType || 'Untitled Beat', // Title
                            beat.description, // Content
                            (beat.type as CardType) || 'plot', // Type
                            realChapterId ? Number(realChapterId) : undefined
                        );
                    }
                });

                await Promise.all(beatPromises);
                toast.success("Timeline updated successfully!");

            } else {
                // Determine real chapter IDs
                const sanitizedBeats = beats.map((beat) => ({
                    ...beat,
                    beatType: beat.title || beat.beatType || 'Untitled Beat', // Ensure beatType is populated for service
                    title: beat.title || beat.beatType || 'Untitled Beat', // Service expects title
                    type: beat.type || 'plot', // Explicitly set type from input
                    chapterId: resolveChapterId(beat.chapterId, chapters)
                }));

                await timelineService.createTimelineWithBeats(
                    storyId.toString(),
                    editableTimeline.name,
                    editableTimeline.description || "",
                    sanitizedBeats as any // Cast to any to avoid strict schema conflicts if service types differ
                );
                toast.success("Timeline created successfully!");
            }
            setIsCollapsed(true);
        } catch (error) {
            console.error("TimelineAction Error:", error);
            const action = editableTimeline?._action === 'update' ? 'update' : 'create';
            toast.error(`Failed to ${action} timeline.`);
        } finally {
            setIsProcessing(false);
        }
    };

    const resolveChapterId = (rawId: string | number | undefined, chaptersList: Partial<Chapter>[]) => {
        if (!rawId) return null;
        // 1. Direct match
        const direct = chaptersList.find(c => c.id === rawId);
        if (direct && direct.id) return direct.id;
        // 2. Index match
        if (typeof rawId === 'number' && rawId > 0 && rawId <= chaptersList.length) {
            return chaptersList[rawId - 1].id || null;
        }
        return null;
    };

    const getChapterName = (chapterId: number) => {
        const c = chapters.find(ch => ch.id === chapterId);
        if (c) return c.title || c.name;
        return `Chapter ${chapterId}`;
    };

    const handleUpdateTimelineField = (field: keyof TimelineToolOutput, value: string) => {
        setHasUserEdited(true);
        setEditableTimeline(prev => prev ? ({ ...prev, [field]: value }) : null);
    };

    const handleUpdateBeat = (index: number, field: string, value: string) => {
        setHasUserEdited(true);
        setEditableTimeline(prev => {
            if (!prev) return null;
            const newBeats = [...(prev.beats || [])];
            newBeats[index] = { ...newBeats[index], [field]: value };
            return { ...prev, beats: newBeats };
        });
    };

    if (isLoading) {
        return (
            <Card className="w-full my-4 border-primary/20 bg-card/50">
                <CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                    </div>
                    <div className="space-y-1">
                        <p className="font-medium text-sm">Thinking...</p>
                        <p className="text-xs text-muted-foreground">Structuring plot points and beats</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!editableTimeline) return null;

    const isUpdate = editableTimeline._action === 'update';
    const beats = editableTimeline.beats || [];
    const hasBeats = Array.isArray(beats) && beats.length > 0;

    return (
        <Card className="w-full my-4 overflow-hidden border">
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors bg-muted/30"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${isUpdate ? 'bg-amber-500/10 text-amber-600' : 'bg-primary/10 text-primary'}`}>
                    {isUpdate ? <RefreshCw className="h-4 w-4" /> : <GitBranch className="h-4 w-4" />}
                </div>
                <div className="flex-grow min-w-0">
                    <p className="font-semibold text-sm truncate">
                        {isUpdate
                            ? `Update Timeline: ${typeof editableTimeline.name === 'object' ? JSON.stringify(editableTimeline.name) : editableTimeline.name}`
                            : `Create Timeline: ${typeof editableTimeline.name === 'object' ? JSON.stringify(editableTimeline.name) : editableTimeline.name}`}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {isUpdate ? "Review changes before applying" : "Review and add to story"}
                    </p>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </Button>
            </div>

            {!isCollapsed && (
                <div className="border-t">
                    <div className="p-4 bg-muted/5 space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground ml-1">Timeline Name</label>
                            <Input
                                value={editableTimeline.name}
                                onChange={(e) => handleUpdateTimelineField('name', e.target.value)}
                                className="font-medium bg-background"
                                placeholder="Timeline Name"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground ml-1">Description</label>
                            <Textarea
                                value={editableTimeline.description || ''}
                                onChange={(e) => handleUpdateTimelineField('description', e.target.value)}
                                className="min-h-[60px] text-sm bg-background"
                                placeholder="Timeline Description"
                            />
                        </div>

                        <div className="flex items-center gap-4 pt-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5 bg-background border px-2.5 py-1 rounded-md shadow-sm">
                                <Target className="h-3.5 w-3.5 text-primary/70" />
                                <span className="font-medium text-foreground">{beats.length} Beats</span>
                            </div>
                            {isUpdate && (
                                <Badge variant="secondary" className="text-[10px] h-5 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
                                    Update Mode
                                </Badge>
                            )}
                        </div>

                        {hasBeats ? (
                            <Accordion type="single" collapsible className="w-full space-y-2" defaultValue="beat-0">
                                {beats.map((beat, index: number) => (
                                    <AccordionItem key={index} value={`beat-${index}`} className="border rounded-lg bg-background px-3 transition-all hover:border-primary/20">
                                        <AccordionTrigger className="hover:no-underline py-3">
                                            <div className="flex flex-col gap-2 w-full">
                                                <div className="flex items-center justify-between w-full pr-2">
                                                    <div className="flex items-center gap-2 text-left w-full pr-4">
                                                        <span className="font-medium text-sm truncate">{beat.title || beat.beatType || `Beat ${index + 1}`}</span>
                                                        {beat.id && <Badge variant="outline" className="text-[10px] shrink-0">ID: {beat.id}</Badge>}
                                                    </div>
                                                    {(beat.chapterName || beat.chapterId) && (
                                                        <Badge variant="secondary" className="text-[10px] h-5 px-2 truncate max-w-[150px] font-medium shrink-0">
                                                            {beat.chapterName || getChapterName(Number(beat.chapterId!))}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pt-1 pb-4 px-1">
                                            <div className="space-y-3">
                                                <div className="grid gap-2">
                                                    <label className="text-xs font-medium text-muted-foreground ml-1">Beat Title / Type</label>
                                                    <Input
                                                        value={beat.title || beat.beatType || ''}
                                                        onChange={(e) => handleUpdateBeat(index, 'title', e.target.value)}
                                                        className="h-8 text-sm"
                                                        placeholder="Beat Title"
                                                    />
                                                </div>
                                                <div className="grid gap-2">
                                                    <label className="text-xs font-medium text-muted-foreground ml-1">Beat Description</label>
                                                    <Textarea
                                                        value={beat.description || ''}
                                                        onChange={(e) => handleUpdateBeat(index, 'description', e.target.value)}
                                                        className="min-h-[80px] text-sm leading-relaxed"
                                                        placeholder="Beat content..."
                                                    />
                                                </div>
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        ) : (
                            <div className="p-8 text-center border-2 border-dashed rounded-xl bg-background/50 text-muted-foreground text-sm">
                                <p>No beats defined for this timeline.</p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 border-t bg-muted/20 px-4 py-3 rounded-b-xl">
                        <Button variant="ghost" size="sm" onClick={() => setIsCollapsed(true)}>
                            Dismiss
                        </Button>
                        <Button
                            onClick={handleAction}
                            disabled={isProcessing}
                            className={`shadow-sm ${isUpdate ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                        >
                            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isUpdate ? (
                                <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Apply Updates
                                </>
                            ) : (
                                <>
                                    <Play className="mr-2 h-4 w-4 fill-current" />
                                    Create Timeline
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
};

const tryParseJSON = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return null;
    }
};
